import {createAsyncThunk, createSelector, createSlice,
	// current
} from "@reduxjs/toolkit"
import {
	// moveNote,
	addNote, addNoteToTree, deleteNote, deleteNoteFromTree,
	// workspaceNoteSlice,
	fetchTopLevelNotes, moveNote, moveNoteInTree
} from "./workspaceNoteSlice"
import { undoDeleteNote } from "./undoDeleteNoteSlice"
import {selectAllowedNoteIds, selectIsLoading} from "./noteRestrictionsSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"


export const setLoadedChildNotesOrder = createAsyncThunk(
	"noteOrder/setLoadedChildNotesOrder",
	async ({flatNotes, workspaceId}) => {
		const orderArray = new Array(Object.keys(flatNotes).length)
		let parentId
		for(let flatNoteIndex in flatNotes) {
			const flatNote = flatNotes[flatNoteIndex]
			if(!parentId){
				parentId = flatNote.parentId
			}
			orderArray[flatNote.order] = flatNote.id
		}
		return {parentId, orderArray, workspaceId}
	}
)

export const clearNotesOrder = createAsyncThunk(
	"noteOrder/clearNotesOrder",
	async (workspaceId, { getState }) => {
		const state = getState()
		const existingByWorkspace = state?.noteOrder?.byWorkspace
		const workspace = ensureWorkspace(existingByWorkspace, workspaceId)
		return workspace.root
	}
)

function insertBeforeTarget(arr, newInt, targetInt) {
	const targetIndex = arr.indexOf(targetInt)

	if (targetIndex === -1) {
		throw new Error("Target integer not found in the array")
	}

	// Create a new array with the new integer inserted before the target integer
	return [...arr.slice(0, targetIndex), newInt, ...arr.slice(targetIndex)]
}

const initialValue = {
	root: []
}
const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialValue)

export const noteOrderSlice = createSlice({
	name: "noteOrder",
	initialState: {
		root: [],
		byWorkspace: {}
	},
	reducers: {
		pushNoteToOrder: (state, action) => {
			const {note, targetNote, pasteAsSibling, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			if (note.parentId) {
				const originalArray = workspace[note.parentId] ? workspace[note.parentId] : []
				workspace[note.parentId] = pasteAsSibling
					? insertBeforeTarget(originalArray, note.id, targetNote.id)
					: [note.id, ...originalArray]
			} else {
				const originalArray = workspace.root
				workspace.root = insertBeforeTarget(originalArray, note.id, targetNote.id)
			}
		},
		setChildOrder: (state, action) => {
			const {parentId, orderArray, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace[parentId] = orderArray
		},
	},
	extraReducers: (builder) => {
		builder.addCase(
			undoDeleteNote.fulfilled,
			(state, action) => {
				const { deletedNote } = action.payload
				const {workspaceId} = deletedNote
				const workspace = ensureWorkspace(state, workspaceId)
				if (deletedNote.path.split(".").length === 1) {
					// create new root array with deleted note in at index 0
					const newRootArray = [deletedNote.id, ...workspace.root]
					workspace.root = newRootArray
				} else {
					// create tmp array for parent order array with deleted note at index 0
					let tmp = workspace[deletedNote.parentId] ? workspace[deletedNote.parentId] : []
					tmp = [deletedNote.id, ...tmp]
					workspace[deletedNote.parentId] = tmp
				}
			}
		)
		builder.addCase(
			fetchTopLevelNotes.fulfilled,
			(state, action) => {
				const {workspaceId, rootOrder} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.root = rootOrder
			}
		)
		builder.addCase(
			setLoadedChildNotesOrder.fulfilled,
			noteOrderSlice.caseReducers.setChildOrder
		)
		builder.addCase(addNote.fulfilled, (state, action) => {
			const { newNote, addingSiblingNote, invokingNote, incomingChildNotes} = action.payload
			const {workspaceId} = newNote
			const workspace = ensureWorkspace(state, workspaceId)
			if(addingSiblingNote) {													// check if we're adding a sibling
				const invokingNoteIndex = workspace[newNote.parentId].findIndex(noteId => noteId === invokingNote.id)
				workspace[newNote.parentId].splice(invokingNoteIndex, 0, newNote.id)		// if adding a sibling, the parent must be in the order slice, so we just splice in the id of the new note
			} else {
				const newNoteisRoot = newNote.parentId === null							// check if we're adding a top-level note
				if(newNoteisRoot) {
					// because we don't have a parent ID to reference or an order value,
					// we have to look at the invoking note to know where to insert the new top-level note
					let siblingRootIndex = workspace.root.findIndex(noteId => noteId === invokingNote.id)
					workspace["root"].splice(siblingRootIndex, 0, newNote.id)			// workspace.root is hardcoded in and will always exist
				} else {
					const noteIdInState = workspace[newNote.parentId] 					// see if the parent note is in the order state
					if(noteIdInState) {
						workspace[newNote.parentId].unshift(newNote.id) 				// if the parent is in the order slice, its a simple splice
					} else {
						let childrenNoteIds = incomingChildNotes.map(note => note.id)// if the note isn't in the order slice, create an array from the ids of its children
						childrenNoteIds.unshift(newNote.id) 						 // new child note goes to the top of the children list
						workspace[newNote.parentId] = childrenNoteIds
					}
				}
			}
		})
		builder.addCase(
			clearNotesOrder.fulfilled,
			(state, action) => {
				const workspaceId = (action.meta.arg)
				return state.byWorkspace[workspaceId] = {root: action.payload}
			}
		)
		builder.addCase(
			moveNote.fulfilled,
			(state, action) => {
				// deconstruct required payload values, set up constants based on the current map's values
				const {
					newNote,
					originalNote,
					incomingChildNotes,
					originalNoteLocation,
					invokingNote,
					workspaceId
				} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				const isMovingFromRoot = originalNoteLocation.parentId === null
				const isMovingToRoot = newNote.parentId === null

				// attempt to find and remove the original note.id from its parent's map in state.
				const originOrderArray = isMovingFromRoot ? workspace["root"] : workspace[originalNoteLocation.parentId]
				if(originOrderArray) {
					const originalNoteIdIndex = originOrderArray.findIndex(noteId => noteId === originalNote.id)
					if(originalNoteIdIndex > -1) {
						originOrderArray.splice(originalNoteIdIndex, 1)
					}
				}

				//attempt to find an existing map of children note IDs for the destination note currently in the state
				const destinationOrderArray = isMovingToRoot ? workspace["root"] : workspace[newNote.parentId]
				if(destinationOrderArray) {							// if current state has a map of children for the destination note...
					if(invokingNote){								// if the action.payload includes an invoking note, then we're responding to a local move, not a networked move
						let invokingNoteIndex = isMovingToRoot ? workspace["root"].findIndex(noteId => noteId === invokingNote.id) : workspace[newNote.parentId].findIndex(noteId => noteId === invokingNote.id) 			// find the invoking note index
						if(invokingNoteIndex === -1) {
							invokingNoteIndex = 0 // if we can't locate the invoking note (often happens handling moves in networked workspaces), insert at index 0
						}
						destinationOrderArray.splice(invokingNoteIndex, 0, newNote.id)												// splice in the new note at the invoking note index
					} else {
						destinationOrderArray.unshift(newNote.id)			// insert the moved note at index 0 of the existing map
					}
					// mutate the state!
					if(isMovingToRoot) {
						workspace["root"] = destinationOrderArray
					} else {
						workspace[newNote.parentId] = destinationOrderArray
					}
				} else {											// if the current state doesn't have a map of children for the destination note
					if(incomingChildNotes) {							// ...but the destination has other children
						let childrenNoteIds = incomingChildNotes.map(note => note.id)	// make a map from the ids of the incoming child notes
						childrenNoteIds.unshift(newNote.id)								// insert the moving note ID at index 0 of the map
						workspace[newNote.parentId] = childrenNoteIds						// set the value in the state to the new map.
					} else {											// the moving note is the first child
						workspace[newNote.parentId] = [newNote.id]							// make a new map for the parent and assign it to the state
					}
				}
			}
		)
		builder.addCase(moveNoteInTree, (state, action) => {
			// deconstruct required payload values, set up constants based on the current map's values
			const {newNote, originalNote, incomingChildNotes, originalNoteLocation, invokingNote} = action.payload
			const {workspaceId} =  newNote
			const workspace = ensureWorkspace(state, workspaceId)
			const isMovingFromRoot = originalNoteLocation.parentId === null
			const isMovingToRoot = newNote.parentId === null
			//remove the original note.id from its parent's map in state
			const originOrderArray = workspace[isMovingFromRoot ? "root" : originalNoteLocation.parentId]
			if(originOrderArray) {
				const originalNoteIdIndex = originOrderArray.findIndex(noteId => noteId === originalNote.id)
				if(originalNoteIdIndex > -1) {
					workspace[isMovingFromRoot ? "root" : originalNoteLocation.parentId].splice(originalNoteIdIndex, 1)
				}
			}
			//attempt to find an existing map of children note IDs for the destination note currently in the workspace
			const destinationOrderArray = workspace[isMovingToRoot ? "root" : newNote.parentId]
			if(destinationOrderArray) {							// if current workspace has a map of children for the destination note...
				if(invokingNote){								// if the action.payload includes an invoking note, then we're responding to a local move, not a networked move
					const invokingNoteIndex = workspace[newNote.parentId].findIndex(noteId => noteId === invokingNote.id) 			// find the invoking note index
					destinationOrderArray.splice(invokingNoteIndex, 0, newNote.id)												// splice in the new note at the invoking note index
				} else {
					destinationOrderArray.unshift(newNote.id)			// insert the moved note at index 0 of the existing map
				}
				workspace[newNote.parentId] = destinationOrderArray			// mutate the workspace!
			} else {											// if the current workspace doesn't have a map of children for the destination note
				if(incomingChildNotes) {							// ...but the destination has other children
					let childrenNoteIds = incomingChildNotes.map(note => note.id)	// make a map from the ids of the incoming child notes
					childrenNoteIds.unshift(newNote.id)								// insert the moving note ID at index 0 of the map
					workspace[newNote.parentId] = childrenNoteIds						// set the value in the workspace to the new map.
				} else {											// the moving note is the first child
					workspace[newNote.parentId] = [newNote.id]							// make a new map for the parent and assign it to the state
				}
			}
		})
		builder.addCase(deleteNote.fulfilled, (state, action) => {
			const {workspaceId} = action.meta.arg
			const {partialDeletedNoteNfo} = action.payload
			const {noteId: deletedNoteId, parentNoteId} = partialDeletedNoteNfo
			const workspace = ensureWorkspace(state, workspaceId)
			const parentNoteMap = workspace[parentNoteId]
			const deletedNoteIndex = parentNoteMap.findIndex(noteId => noteId === deletedNoteId)
			workspace[parentNoteId].splice(deletedNoteIndex, 1)
		})
		builder.addCase(addNoteToTree, (state, action) => {
			const newNote = action.payload
			const {workspaceId} = newNote
			const workspace = ensureWorkspace(state, workspaceId)
			const newNoteIsRoot = newNote.parentId === null

			if(newNoteIsRoot) {
				workspace["root"].push(newNote.id)
			} else {
				const newNoteParentInState = workspace[newNote.parentId]
				if(newNoteParentInState) {
					workspace[newNote.parentId].unshift(newNote.id)
				}
				// else, if the parent isn't in the order map, it doesn't matter because the client will load in the children when the user opens the parent
			}
		})
		builder.addCase(deleteNoteFromTree, (state, action) => {
			const {parentId, id: noteId, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			const parentNoteMap = workspace[parentId !== null ? parentId : "root"]
			if(parentNoteMap) {
				const deletedNoteIndex = parentNoteMap.findIndex(mapNoteId => mapNoteId === noteId)
				workspace[parentId !== null ? parentId : "root"].splice(deletedNoteIndex, 1)
			}
			// else, if the parent isn't in the order map, it doesn't matter because the client will load in the children when the user opens the parent
		})
	},
})

export const { pushNoteToOrder } = noteOrderSlice.actions

export const selectAllNoteOrders = (state, workspaceId) => state.noteOrder.byWorkspace[workspaceId] ?? initialValue

export const makeSelectNotesOrder = () => createSelector(
	//parentId is a parameter passed in when this selector is invoked, we fake it as an existing selector for Redux Toolkit
	[
		(state, workspaceId) => selectAllNoteOrders(state, workspaceId),
		(state, workspaceId) => selectAllowedNoteIds(state, workspaceId),
		(state, workspaceId) => selectIsLoading(state, workspaceId),
		(state, workspaceId, parentId) => parentId
	],
	(noteOrders, notePermissions, notePermissionsLoading, parentId) => {
		if(notePermissionsLoading) return []

		const hasFullAccess = notePermissions.length === 0
		if(parentId){
			if(parentId === "root" && !hasFullAccess){
				return noteOrders["root"].filter(noteId => notePermissions.includes(noteId)) ?? []
			} else {
				return noteOrders[parentId] ?? []
			}
		} else {
			const rootOrders = noteOrders.root ?? []
			const filteredRootOrders = rootOrders.filter(noteId => notePermissions.includes(noteId))
			return filteredRootOrders.length && !hasFullAccess ? filteredRootOrders : filteredRootOrders.length ? rootOrders : []
		}

	}
)

export default noteOrderSlice.reducer
